/**
 * cart-drawer.js
 * Load file này ở cuối layout/theme.liquid, sau khi cart-drawer snippet đã render trong DOM:
 * {{ 'cart-drawer.js' | asset_url | script_tag }}
 *
 * Cách mở drawer từ bất kỳ nơi nào trong theme (JS khác, form add-to-cart, v.v.):
 *   document.dispatchEvent(new CustomEvent('cart:open'));
 *
 * Cách chỉ làm mới nội dung drawer (không mở) sau khi đổi giỏ hàng ở nơi khác:
 *   document.dispatchEvent(new CustomEvent('cart:refresh'));
 */

class CartDrawer extends HTMLElement {
  constructor() {
    super();
    this.body = this.querySelector('[data-cart-drawer-body]');
    this.subtotal = this.querySelector('[data-cart-drawer-subtotal]');

    this.shippingText = this.querySelector('[data-shipping-text]');
    this.shippingFill = this.querySelector('[data-shipping-fill]');
    // Đọc từ config settings.free_shipping_threshold (đã convert sang cents ở liquid)
    this.freeShippingThreshold = parseInt(this.dataset.freeShippingThreshold, 10) || 0;

    this.querySelectorAll('[data-cart-drawer-close]').forEach((btn) => {
      btn.addEventListener('click', () => this.close());
    });

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.classList.contains('is-open')) this.close();
    });

    document.addEventListener('cart:open', () => this.open());
    document.addEventListener('cart:refresh', () => this.renderCart());

    // Bất kỳ link/nút nào có data-cart-drawer-open sẽ mở drawer thay vì đi tới /cart
    document.querySelectorAll('[data-cart-drawer-open]').forEach((link) => {
      link.addEventListener('click', (e) => {
        e.preventDefault();
        this.open();
      });
    });
  }

  open() {
    this.classList.add('is-open');
    document.body.classList.add('cart-drawer-is-open');
    this.renderCart();
  }

  close() {
    this.classList.remove('is-open');
    document.body.classList.remove('cart-drawer-is-open');
  }

  async fetchCart() {
    const res = await fetch('/cart.js');
    return res.json();
  }

  async renderCart() {
    const cart = await this.fetchCart();
    this.paintCart(cart);
  }

  paintCart(cart) {
    this.updateBubble(cart.item_count);
    this.renderShippingBar(cart.total_price);
    this.querySelectorAll('[data-cart-drawer-count]').forEach((el) => {
      el.textContent = `${cart.item_count} Artikel`;
    });

    if (cart.item_count === 0) {
      this.body.innerHTML = '<p class="cart-drawer__empty">Dein Warenkorb ist leer.</p>';
      this.subtotal.textContent = this.formatMoney(0);
      return;
    }

    this.body.innerHTML = cart.items.map((item) => this.renderLineItem(item)).join('');
    this.subtotal.textContent = this.formatMoney(cart.total_price);
    this.bindLineItemEvents();
  }

  renderShippingBar(totalPrice) {
    if (!this.shippingText || !this.shippingFill) return;
    if (this.freeShippingThreshold <= 0) return; // config = 0 nghĩa là tắt tính năng này

   
    const remaining = this.freeShippingThreshold - totalPrice;
    const percent = Math.min(100, (totalPrice / this.freeShippingThreshold) * 100);
    this.shippingFill.style.width = `${percent}%`;

    if (remaining <= 0) {
      this.shippingText.textContent = '🎉 Du hast gratis Versand freigeschaltet!';
      this.shippingFill.classList.add('is-complete');
    } else {
      this.shippingText.innerHTML = `Nur noch <strong>${this.formatMoney(remaining)}</strong> bis gratis Versand!`;
      this.shippingFill.classList.remove('is-complete');
    }
  }

  renderLineItem(item) {
    return `
      <div class="cart-drawer__item" data-line-key="${item.key}">
        <a href="${item.url}" class="cart-drawer__item-image">
          <img src="${item.image}&width=160" alt="${item.product_title}" loading="lazy" width="80" height="80">
        </a>
        <div class="cart-drawer__item-info">
          <a href="${item.url}" class="cart-drawer__item-title">${item.product_title}</a>
          ${item.variant_title ? `<p class="cart-drawer__item-variant">${item.variant_title}</p>` : ''}
          <p class="cart-drawer__item-price">${this.formatMoney(item.final_price)}</p>
          <div class="cart-drawer__item-qty">
            <button type="button" class="cart-drawer__qty-btn" data-qty-decrease aria-label="Menge verringern">−</button>
            <input type="number" class="cart-drawer__qty-input" min="0" value="${item.quantity}" data-qty-input aria-label="Menge">
            <button type="button" class="cart-drawer__qty-btn" data-qty-increase aria-label="Menge erhöhen">+</button>
          </div>
        </div>
        <button type="button" class="cart-drawer__item-remove" data-remove-item aria-label="Entfernen">&times;</button>
      </div>
    `;
  }

  bindLineItemEvents() {
    this.body.querySelectorAll('[data-line-key]').forEach((row) => {
      const key = row.dataset.lineKey;
      const input = row.querySelector('[data-qty-input]');

      row.querySelector('[data-qty-decrease]').addEventListener('click', () => {
        this.updateLine(key, Math.max(0, parseInt(input.value, 10) - 1));
      });
      row.querySelector('[data-qty-increase]').addEventListener('click', () => {
        this.updateLine(key, parseInt(input.value, 10) + 1);
      });
      input.addEventListener('change', () => {
        this.updateLine(key, Math.max(0, parseInt(input.value, 10) || 0));
      });
      row.querySelector('[data-remove-item]').addEventListener('click', () => {
        this.updateLine(key, 0);
      });
    });
  }

  async updateLine(key, quantity) {
    this.body.classList.add('cart-drawer__body--loading');
    const res = await fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: key, quantity }),
    });
    const cart = await res.json();
    this.body.classList.remove('cart-drawer__body--loading');
    this.paintCart(cart);
  }

  updateBubble(count) {
    document.querySelectorAll('.header__cart-count').forEach((bubble) => {
      const sup = bubble.querySelector('sup');
      if (sup) sup.textContent = count;
      bubble.style.display = count === 0 ? 'none' : '';
    });
  }

  formatMoney(cents) {
    return (cents / 100).toLocaleString('de-DE', { style: 'currency', currency: 'EUR' });
  }
}

customElements.define('cart-drawer', CartDrawer);
